import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "siding-web-design-colorado",
  metaTitle: "Siding Contractor Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom siding web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Siding Contractor Web Design Colorado",
  eyebrow: "Siding Contractor Web Design — Colorado",
  h1: (
    <>
      Siding Contractor <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded siding websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Siding websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first siding sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Siding Contractor web design in Colorado",
    body: "Siding Contractor web design is the practice of building conversion-focused, mobile-first websites for siding businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with portfolio galleries that don't tank PageSpeed." },
    { title: "Material-type service pages", body: "James Hardie, LP SmartSide, vinyl, steel, stucco, cedar — each ranks independently." },
    { title: "Portfolio galleries", body: "Lazy-loaded project galleries with image schema." },
    { title: "Insurance claim guidance", body: "CO hail and wind claim guidance captures long-tail research traffic." },
    { title: "Quote forms with claim-status fields", body: "Qualifying intake with insurance claim status to fast-track real buyers." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Insurance claims drive CO siding work", body: "Hail and wind claims dominate CO siding demand. Claim-guidance content captures researchers." },
    { heading: "Material-type pages outrank generic siding", body: "James Hardie vs LP SmartSide vs vinyl are different buyers and different searches." },
    { heading: "Portfolio + brand certifications close the bid", body: "James Hardie Preferred Contractor badge + portfolio above the fold close the estimate." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a siding website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a siding website?", a: "Typical timeline is 4–6 weeks for a 12–20 page siding site with full service and service-area pages. Larger multi-location siding businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs siding businesses?", a: "No — we build siding sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Siding Contractor Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for siding businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/siding-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
